/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useMemo, useRef } from 'react';

import { Typography } from '@remarkable/ark-web';
import { getRouteApi } from '@tanstack/react-router';
import {
  functionalUpdate,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useDebouncedCallback } from 'use-debounce';

import type { FleetMemberRow } from 'src/api/endpoints/cloudEnterprise.types';
import { useEnterpriseInvitations } from 'src/api/queries/enterpriseInvitations';
import { useFleetMembers } from 'src/api/queries/fleet';
import { useAccessTokenClaims } from 'src/api/queries/user';
import { Button, Input, SomethingWentWrong, Table } from 'src/components';
import { DeviceIcon } from 'src/components/DeviceIcon';
import { Ellipsis } from 'src/components/Ellipsis';
import { IntegrationIcon } from 'src/components/IntegrationIcon';
import { TimeSinceNow } from 'src/components/TimeSinceNow';
import { useMultiRowSelect } from 'src/utils/createTableSelectColumn';
import { ensureUniqueKeys } from 'src/utils/ensureUniqueKeys';
import { tableHelpers } from 'src/utils/tableHelpers';

import { ChevronRight, Import, X } from '@remarkable/ark-icons';
import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { CopyButton } from 'src/components/CopyButton';
import { TableBulkActionsPanel } from 'src/components/TableBulkActionsPanel';
import { downloadCSV } from 'src/utils/downloadCsv';
import { userHelpers } from 'src/utils/userHelpers';
import { EnterpriseDeviceIcon } from './EnterpriseDeviceIcon';
import { fleetMemberRowHelpers } from './fleetMemberRowHelpers';
import { FleetTableBody } from './FleetTableBody';
import { FleetTableColumnFilterSelector } from './FleetTableColumnFilterSelector';
import { createFleetTableColumns } from './fleetTableColumns';
import { fleetUserHelpers } from './fleetUserHelpers';
import { InviteToEnterpriseButton } from './InviteToEnterpriseButton';

const routeApi = getRouteApi('/_auth/enterprise/_with-org/_layout/members');

export const FleetTable = () => {
  const members = useFleetMembers();
  const invitations = useEnterpriseInvitations();
  const accessTokenClaims = useAccessTokenClaims();
  const memberRows: FleetMemberRow[] = useMemo(() => {
    const mappedInvitations =
      invitations.data?.invitations?.map(
        (i) =>
          ({
            type: 'invitation',
            data: i,
          }) as const
      ) ?? [];
    const mappedUsers =
      members.data?.users?.map((u) => ({ type: 'user', data: u }) as const) ??
      [];

    return [...mappedInvitations, ...mappedUsers].toSorted((a, b) => {
      // sort undefined emails to the end
      if (!a.data.email) return 1;
      if (!b.data.email) return -1;

      return a.data.email.localeCompare(b.data.email);
    });
  }, [members.data, invitations.data]);

  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const currentUserEmail = userHelpers.getEmailFromClaims(
    accessTokenClaims.data
  );
  const currentUserOwnerRole = useMemo(() => {
    const currentUser = members.data?.users?.find(
      (user) => user.email === currentUserEmail
    );

    return currentUser?.roles?.find(
      (role) => fleetUserHelpers.getRoleType(role.name) === 'OWNER'
    );
  }, [members.data?.users, currentUserEmail]);

  const fleetTableColumns = useMemo(
    () =>
      createFleetTableColumns({
        currentUserEmail,
        currentUserOwnerRole: currentUserOwnerRole ?? null,
      }),
    [currentUserEmail, currentUserOwnerRole]
  );

  const columnFilters = useMemo(() => {
    return tableHelpers.paramsToFilters({
      integrations: search.integrations,
      roles: search.roles,
      type: search.type,
      status: search.status,
      'device-software-versions': search['device-software-versions'],
      beta: search.beta,
    });
  }, [
    search.devices,
    search.integrations,
    search.roles,
    search.type,
    search.status,
    search['device-software-versions'],
    search.beta,
  ]);

  const debouncedGlobalFilter = useDebouncedCallback((value: string) => {
    void navigate({
      to: '.',
      search: { ...search, filter: value.trim() || undefined },
      replace: true,
    });
  }, 100);

  const tableRef = useRef(null);

  const table = useReactTable({
    data: memberRows,
    columns: fleetTableColumns,
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
    state: {
      globalFilter: search.filter?.trim() || undefined,
      columnFilters,
      columnVisibility: {
        type: false,
        'device-software-versions': false,
        beta: false,
      },
    },
    onGlobalFilterChange: (updater: string | undefined) => {
      void navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          filter: functionalUpdate(updater, prev.filter)?.trim() || undefined,
        }),
        replace: true,
      });
    },
    onColumnFiltersChange: (updater) => {
      const newColumnFilters = functionalUpdate(updater, columnFilters);

      const filterParams = tableHelpers.filtersToParams(newColumnFilters, {
        devices: undefined,
        integrations: undefined,
        roles: undefined,
        type: undefined,
        status: undefined,
        'device-software-versions': undefined, // needs more tweaking
        beta: undefined,
      });

      void navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          ...filterParams,
        }),
        replace: true,
      });
    },
    meta: {
      ...useMultiRowSelect<FleetMemberRow>(),
      fillRowsToMatchPageSize: false,
      getRowLabel: fleetMemberRowHelpers.getRowLabel,
      getRowDescription: (row) =>
        fleetMemberRowHelpers.isUser(row)
          ? 'Press Enter to open the member details'
          : undefined,
    },
    enableFilters: true,
    getRowId: (data, index) => data.data.id || index.toString(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: getPaginationRowModel(),
    renderFallbackValue: '-',
  });

  if (members.isError) {
    return <SomethingWentWrong error={members.error} />;
  }

  return (
    <div className="flex flex-col gap-16 py-16">
      <div className="flex flex-wrap-reverse items-end justify-between gap-16">
        <div className="flex w-full flex-col gap-8 ls:w-auto">
          <div className="flex w-full items-center gap-8 ls:w-auto">
            <Input
              id="search"
              placeholder="Search"
              className="w-full ls:max-w-[300px]"
              defaultValue={search.filter}
              onChange={(e) => debouncedGlobalFilter(e.currentTarget.value)}
            />

            <FleetTableColumnFilterSelector
              componentLocation={ComponentLocations.ENTERPRISE.MEMBERS_PAGE}
              table={table}
              columns={['type', 'roles', 'integrations', 'beta', 'status']}
            />
          </div>

          <div className="rounded-sm px-8 py-4">
            <Typography
              variant="interface-sm"
              leadingNone
              uppercase
              className="opacity-70"
            >
              {table.getFilteredRowModel().flatRows.length} rows
            </Typography>
          </div>
        </div>

        <div className="flex w-full items-center justify-end gap-16 ls:w-auto">
          <InviteToEnterpriseButton />
        </div>
      </div>

      <div ref={tableRef} className="w-full">
        <Table
          table={table}
          data-cy="members-table"
          className="hidden w-full lxl:table"
          loading={members.isPending}
          pagination
        >
          <FleetTableBody table={table} />
        </Table>
      </div>

      <ul
        data-cy="members-list-mobile"
        className="border-t border-gray-100 lxl:hidden"
      >
        {table.getRowModel().rows.map((row) => (
          // We use the table's row state because it is already filtered and sorted
          <li
            data-cy={row.id}
            key={row.id}
            className={clsx(
              'cursor-pointer border-b border-gray-100 py-12 hover:bg-paper-3',
              {
                'bg-error-subtle hover:bg-error-subtle':
                  fleetMemberRowHelpers.isMdmDeactivated(row.original),
              }
            )}
            onClick={() => {
              if (search.member !== row.original.data.email) {
                tracker.trackEvent(
                  new ButtonClicked({
                    component_location:
                      ComponentLocations.ENTERPRISE.MEMBERS_PAGE_MOBILE,
                    text: '[member table row]',
                    action: 'open member details drawer',
                  })
                );
                void navigate({
                  search: { member: row.original.data.email },
                  replace: true,
                });
              } else {
                tracker.trackEvent(
                  new ButtonClicked({
                    component_location:
                      ComponentLocations.ENTERPRISE.MEMBERS_PAGE_MOBILE,
                    text: '[member table row]',
                    action: 'close member details drawer',
                  })
                );
                void navigate({ search: { member: undefined }, replace: true });
              }
            }}
          >
            <div className="flex items-center justify-between px-8">
              <div className="mr-8 flex w-fit shrink grow flex-col truncate">
                <Typography variant="body-sm" className="truncate">
                  {fleetMemberRowHelpers.getName(row.original) ||
                    row.original.data.email ||
                    'No name'}
                </Typography>
                <Typography
                  variant="interface-xs"
                  leadingNone
                  className="text-neutral-dark-1"
                >
                  Last seen:{' '}
                  <TimeSinceNow
                    date={fleetMemberRowHelpers.getLastSeenDate(row.original)}
                  />
                </Typography>
                {fleetMemberRowHelpers.getRoles(row.original).length > 0 ? (
                  <Typography
                    variant="interface-xs"
                    leadingNone
                    className="text-neutral-dark-1"
                  >
                    <>
                      <span className="text-muted">
                        {fleetMemberRowHelpers
                          .getRoleNames(row.original)
                          .join(', ')}
                      </span>
                    </>
                  </Typography>
                ) : null}
              </div>

              <Ellipsis className="flex w-fit min-w-0 shrink-[10] items-center gap-4">
                {ensureUniqueKeys(
                  fleetMemberRowHelpers.getDevices(row.original),
                  (d) => d.serialNumber
                ).map((d) => (
                  <div key={d.key} className="shrink-0">
                    <EnterpriseDeviceIcon
                      device={d}
                      active={fleetMemberRowHelpers.shouldHighlightDevice(
                        search.filter,
                        search.devices,
                        d
                      )}
                    />
                  </div>
                ))}
                {ensureUniqueKeys(
                  fleetMemberRowHelpers.getCompanions(row.original),
                  (c) => c.serialNumber
                ).map((c) => (
                  <div className="shrink-0" key={c.key}>
                    <DeviceIcon device={c} />
                  </div>
                ))}
                {fleetMemberRowHelpers
                  .getIntegrations(row.original)
                  .map((i) => (
                    <div
                      className="min-w-fit shrink-0"
                      key={
                        (i.provider ?? '') +
                        // eslint-disable-next-line @typescript-eslint/no-base-to-string
                        (i.createdAt ?? indexedDB.toString())
                      }
                    >
                      <IntegrationIcon className="h-16" provider={i.provider} />
                    </div>
                  ))}
              </Ellipsis>

              <ChevronRight
                size={20}
                className="shrink-0 text-neutral-dark-1"
              />
            </div>
          </li>
        ))}
      </ul>

      <TableBulkActionsPanel
        table={table}
        className="flex justify-between px-16"
      >
        <div className="flex items-center gap-16">
          <Typography variant="interface-sm" leadingNone uppercase>
            {table.getSelectedRowModel().rows.length} users selected
          </Typography>
        </div>

        <div className="flex items-center gap-16">
          <CopyButton
            size="medium"
            variant="tertiary-neutral"
            value={table
              .getSelectedRowModel()
              .flatRows.map((r) => r.original.data.email)
              .join('\n')}
            className="flex-row-reverse"
            onClick={() => {
              tracker.trackEvent(
                new ButtonClicked({
                  component_location:
                    ComponentLocations.ENTERPRISE.MEMBERS_BULK_ACTIONS,
                  text: 'Copy emails',
                  action: 'copy selected member emails',
                })
              );
            }}
          >
            Copy emails
          </CopyButton>

          <Button
            size="medium"
            variant="secondary"
            analytics={{
              location: ComponentLocations.ENTERPRISE.MEMBERS_BULK_ACTIONS,
              text: 'Download CSV',
              action: 'download selected members as csv',
            }}
            onClick={() => {
              const selectedRows = table
                .getSelectedRowModel()
                .flatRows.map((r) => {
                  const Type = r.original.type;
                  const Email = r.original.data.email;
                  const Name = fleetMemberRowHelpers.getName(r.original);

                  return {
                    Type,
                    Email,
                    Name: Name !== Email ? Name : '',
                  };
                });

              downloadCSV(selectedRows, 'members.csv', ',');
            }}
          >
            <Import /> <span>Download CSV</span>
          </Button>

          <Button
            variant="tertiary-neutral"
            size="medium"
            onClick={() => {
              table.setRowSelection({});
            }}
            analytics={{
              location: ComponentLocations.ENTERPRISE.MEMBERS_BULK_ACTIONS,
              text: '[X icon]',
              action: 'close bulk actions panel',
            }}
            className="!rounded"
          >
            <X />
          </Button>
        </div>
      </TableBulkActionsPanel>
    </div>
  );
};
